import moment from 'moment';
import { debounce } from 'lodash';
import {
    Button,
    Col,
    Input,
    Row,
    Select,
    Space,
    Table,
    Tooltip,
    Typography,
} from 'antd';
import {
    useContext,
    useState,
    FC,
    useCallback,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';
import { IoMdRefresh } from 'react-icons/io';
import VariableModal from '../../Modals/VariableModal';
import { VariableContext } from '../context/VariableContext';
import { IVariable } from '../../../interfaces/plan';

const { Text } = Typography;
const { Search } = Input;

interface Props {
    filters?: boolean
    pagination?: boolean
}

const BookingVariables: FC<Props> = ({ filters = false, pagination = false }) => {
    const [variable, setVariable] = useState<any>();
    const [showVariable, setShowVariable] = useState(false);

    const [searchParams] = useSearchParams();
    const search = searchParams.get('search') || '';
    const origin = JSON.parse(searchParams?.get('origin') || '[]');

    const { id } = useParams();

    const {
        variables,
        loading,
        total,
        paginator,
        handleSearchParam,
        setPaginator,
        addVariable,
        refreshData,
    } = useContext(VariableContext);

    const TYPES = [
        { label: 'MRR', value: 'MRR' },
        { label: 'BOOKING', value: 'BOOKING' },
    ];

    const menu = (variableId: string) => (
        <Row justify="center" gutter={12}>
            <Col>
                <Tooltip title="Edit">
                    <Button
                        icon={<FaEdit style={{ verticalAlign: 'middle', color: '#177DDC' }} />}
                        type="ghost"
                        onClick={() => {
                            setVariable(variables.find((v: any) => v._id === variableId));
                            setShowVariable(true);
                        }}
                    />
                </Tooltip>
            </Col>
        </Row>
    );

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, variable: IVariable) => (
                (variable.planID || !id) ? (
                    <a
                        style={{ color: '#87d068' }}
                        onClick={() => {
                            setVariable({ ...variable });
                            setShowVariable(true);
                        }}
                    >
                        {name}
                    </a>
                ) : (
                    <Text style={{ color: '#177DDC' }}>
                        {name}
                    </Text>
                )
            ),
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center' as 'center',
            render: (createdAt: string) => (
                <Text>
                    {moment(createdAt).format('MMM DD MMM')}
                </Text>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            align: 'center' as 'center',
            render: (type: string) => (
                <Text>{type || 'Not available'}</Text>
            ),
        },
        {
            title: 'Used for',
            dataIndex: 'origin',
            key: 'origin',
            align: 'center' as 'center',
            render: (origin: string) => (
                <Text>{origin || 'Not available'}</Text>
            ),
        },
        {
            title: 'Is Global',
            dataIndex: 'planID',
            key: 'planID',
            align: 'center' as 'center',
            render: (planID: string) => (
                <Text>{(planID) ? 'No' : 'Yes'}</Text>
            ),
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            width: 1000,
            render: (value: string) => (
                <Tooltip title={value}>
                    <Text style={{
                        maxWidth: 1000,
                        display: 'inline-block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                    >
                        {value || 0.00}
                    </Text>
                </Tooltip>
            ),
        },
        {
            title: '',
            dataIndex: '_id',
            key: 'actions',
            width: 60,
            fixed: 'right' as 'right',
            render: (_id: string, variable: IVariable) => ((variable.planID || !id) ? menu(_id) : <Space />),
        },
    ];

    const handleAddVariable = () => {
        setShowVariable(true);
        setVariable({ planID: id });
    };

    const handleCancelVariable = (variable: any) => {
        if (variable) {
            addVariable(variable);
        }
        setShowVariable(false);
    };

    const onPageChange = (page: number, pageSize: number) => setPaginator({
        limit: pageSize,
        page,
    });

    const handleTypeChange = (value: string[]) => {
        handleSearchParam('origin', value);
    };

    const handleSearch = useCallback(
        debounce((e: any) => {
            const value = e.target.value;
            handleSearchParam('search', value);
        }, 600),
        [handleSearchParam],
    );

    return (
        <div style={{ padding: '32px 0' }}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Row justify={filters ? 'space-between' : 'end'} gutter={16}>
                    {
                        showVariable && (
                            <VariableModal
                                visible={showVariable}
                                variable={variable}
                                onCancel={handleCancelVariable}
                            />
                        )
                    }
                    {
                        filters && (
                            <Col>
                                <Space size={16}>
                                    <Search
                                        onChange={handleSearch}
                                        enterButton
                                        allowClear
                                        placeholder="Search"
                                        defaultValue={search}
                                    />
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        options={TYPES}
                                        placeholder="Select a type"
                                        style={{ width: '300px' }}
                                        onChange={handleTypeChange}
                                        value={origin}
                                    />
                                    <Button
                                        type="ghost"
                                        onClick={refreshData}
                                        icon={(
                                            <IoMdRefresh
                                                size={16}
                                                style={{ verticalAlign: 'middle' }}
                                            />
                                        )}
                                    />
                                </Space>
                            </Col>
                        )
                    }
                    <Col>
                        <Button type="primary" onClick={handleAddVariable}>
                            Add Variable
                        </Button>
                    </Col>
                </Row>
                <Table
                    columns={columns}
                    dataSource={variables}
                    loading={loading}
                    rowKey="_id"
                    locale={{ emptyText: 'No variables' }}
                    pagination={pagination ? {
                        total,
                        current: paginator.page,
                        pageSize: paginator.limit,
                        onChange: onPageChange,
                        showSizeChanger: true,
                    } : false}
                />
            </Space>
        </div>
    );
};

export { BookingVariables };
